import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft } from 'lucide-react';
import { MODAL_COLORS } from '@/components/modal';
import { AUTH_COLORS } from '@/constants/theme';
import { useAuth } from '../hooks/useAuth';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import { FloatingField } from '@/components/ui/FloatingField';

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const { forgotPassword, forgotPasswordLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useMediaQuery(MD_BREAKPOINT);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    forgotPassword(email, {
      onSuccess: () => setSent(true),
      onError: (err: unknown) => {
        const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
        setError(msg || t('auth.forgotPasswordError'));
      },
    });
  };

  const inputStyle = {
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255, 255, 255, 0.5)',
    color: '#0d2b6b',
  };

  const formContent = (
    <>
      <Link
        to="/login"
        className="inline-flex items-center gap-1 text-sm font-bold"
        style={{ color: '#0d2b6b' }}
      >
        <ArrowLeft size={16} />
        {t('auth.backToLogin')}
      </Link>

      <div className="text-center">
        <h1 className="text-2xl font-extrabold tracking-tight" style={{ color: '#0d2b6b' }}>
          {t('auth.forgotPassword')}
        </h1>
        <p className="mt-2 text-sm font-medium" style={{ color: '#1a4fa0', opacity: 0.7 }}>
          {t('auth.forgotPasswordSubtitle')}
        </p>
      </div>

      {sent ? (
        <div
          className="rounded-xl p-4 text-sm font-semibold text-center"
          style={{
            backgroundColor: '#ecfdf5',
            border: `1px solid ${MODAL_COLORS.border}`,
            color: '#065f46',
          }}
        >
          {t('auth.forgotPasswordSuccess')}
        </div>
      ) : (
        <>
          {error && (
            <div
              className="rounded-xl p-3 text-sm font-bold"
              style={{
                backgroundColor: AUTH_COLORS.errorBg,
                border: `1px solid ${MODAL_COLORS.border}`,
                color: AUTH_COLORS.errorText,
              }}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <FloatingField
              label={t('auth.email')}
              inputId="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              inputClassName="focus:ring-2 focus:ring-primary-500"
              inputStyle={inputStyle}
            />

            <button
              type="submit"
              disabled={forgotPasswordLoading}
              className="w-full rounded-xl py-3 text-sm font-bold transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: 'linear-gradient(135deg, #1a4fa0 0%, #3b8dd4 100%)',
                color: '#ffffff',
                border: '1px solid rgba(255,255,255,0.2)',
                boxShadow: '0 4px 15px rgba(13,43,107,0.3)',
              }}
            >
              {forgotPasswordLoading ? t('common.loading') : t('auth.sendResetLink')}
            </button>
          </form>
        </>
      )}
    </>
  );

  if (isMobile) {
    return (
      <div className="login-mesh-bg fixed inset-0" style={{ position: 'fixed' }}>
        <div
          className="relative z-10 h-full overflow-y-auto flex flex-col justify-center px-5 py-8 space-y-6"
          style={{
            backgroundColor: 'rgba(255,255,255,0.55)',
            backdropFilter: 'blur(20px) saturate(180%)',
          }}
        >
          {formContent}
        </div>
      </div>
    );
  }

  return (
    <div className="login-mesh-bg fixed inset-0 flex items-center justify-center" style={{ position: 'fixed' }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="relative z-10 w-full max-w-md"
      >
        <div
          className="w-full space-y-6 rounded-3xl p-8"
          style={{
            backgroundColor: 'rgba(255,255,255,0.65)',
            backdropFilter: 'blur(20px) saturate(180%)',
            border: '1px solid rgba(255,255,255,0.4)',
            boxShadow: '0 8px 32px rgba(13,43,107,0.15), inset 0 1px 0 rgba(255,255,255,0.5)',
          }}
        >
          {formContent}
        </div>
      </motion.div>
    </div>
  );
}
